import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FiImage, FiX, FiLinkedin, FiAlertCircle, FiUser, FiBriefcase, FiRepeat } from 'react-icons/fi';
import { PLATFORMS, ACCOUNTS, REPOST_DELAY_OPTIONS } from '../../services/SocialPostService';

const PLATFORM_CONFIG = {
  linkedin: {
    icon: FiLinkedin,
    color: 'text-[#0A66C2]',
    bg: 'bg-[#0A66C2]/10',
    ring: 'ring-[#0A66C2]/30',
    label: 'LinkedIn',
    charLimit: 3000,
    placeholder: 'Share insights, updates, or ideas with your LinkedIn audience...',
  },
  instagram: {
    icon: FiImage,
    color: 'text-[#E1306C]',
    bg: 'bg-gradient-to-br from-[#F58529]/10 via-[#DD2A7B]/10 to-[#8134AF]/10',
    ring: 'ring-[#DD2A7B]/30',
    label: 'Instagram',
    charLimit: 2200,
    placeholder: 'Write your Instagram caption...',
  },
};

const ACCOUNT_CONFIG = {
  personal: { icon: FiUser, label: 'Personal', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-200' },
  business: { icon: FiBriefcase, label: 'Business', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-200' },
};

export default function PostComposer({
  content = '',
  platform = 'linkedin',
  account = 'business',
  mediaUrl = null,
  repostConfig = null,
  onContentChange,
  onPlatformChange,
  onAccountChange,
  onMediaChange,
  onMediaRemove,
  onRepostConfigChange,
  disabled = false,
  errors = {},
  compact = false,
}) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.linkedin;
  const charLimit = config.charLimit;
  const charCount = content.length;
  const charPercent = (charCount / charLimit) * 100;

  const charColor = charPercent >= 100
    ? 'text-red-600'
    : charPercent >= 90
      ? 'text-amber-600'
      : 'text-zinc-400';

  const ringColor = charPercent >= 100 ? 'ring-red-300 border-red-300' : '';

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(compact ? 120 : 180, textareaRef.current.scrollHeight)}px`;
    }
  }, [content, compact]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0] && onMediaChange) {
      onMediaChange(e.dataTransfer.files[0]);
    }
  }, [onMediaChange]);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files?.[0] && onMediaChange) {
      onMediaChange(e.target.files[0]);
    }
  }, [onMediaChange]);

  const otherAccount = account === 'personal' ? 'business' : 'personal';
  const repostEnabled = repostConfig?.enabled || false;
  const repostDelay = repostConfig?.delay_minutes ?? 0;

  return (
    <div className="space-y-4">
      {/* Platform + Account selectors */}
      {!compact && (
        <div className="flex items-center gap-4 flex-wrap">
          {/* Platform */}
          {onPlatformChange && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Platform</span>
              <div className="flex gap-1.5">
                {Object.keys(PLATFORMS).map((key) => {
                  const pc = PLATFORM_CONFIG[key];
                  if (!pc) return null;
                  const Icon = pc.icon;
                  const isActive = platform === key;
                  return (
                    <button
                      key={key}
                      onClick={() => onPlatformChange(key)}
                      disabled={disabled || key === 'instagram'}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                        ${isActive ? `${pc.bg} ${pc.color} ring-1 ${pc.ring}` : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'}
                        ${key === 'instagram' ? 'opacity-40 cursor-not-allowed' : ''}
                      `}
                      title={key === 'instagram' ? 'Coming soon' : pc.label}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {pc.label}
                      {key === 'instagram' && <span className="text-[9px] opacity-70">Soon</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Account */}
          {onAccountChange && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Account</span>
              <div className="flex gap-1.5">
                {Object.keys(ACCOUNTS).map((key) => {
                  const ac = ACCOUNT_CONFIG[key];
                  const Icon = ac.icon;
                  const isActive = account === key;
                  return (
                    <button
                      key={key}
                      onClick={() => onAccountChange(key)}
                      disabled={disabled}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                        ${isActive ? `${ac.bg} ${ac.color} ring-1 ${ac.ring}` : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'}
                      `}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {ac.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Text editor */}
      <div className={`relative ${errors.content ? 'ring-2 ring-red-300 rounded-xl' : ''}`}>
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => onContentChange?.(e.target.value)}
          placeholder={config.placeholder}
          disabled={disabled}
          className={`
            w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none
            transition-all duration-200 placeholder:text-zinc-400
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${ringColor || 'border-zinc-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-zinc-300'}
            ${disabled ? 'bg-zinc-50 text-zinc-500 cursor-not-allowed' : 'bg-white text-zinc-900'}
          `}
          style={{ minHeight: compact ? 120 : 180 }}
        />
        <div className={`absolute bottom-3 right-3 flex items-center gap-2 ${charColor}`}>
          {charPercent >= 90 && <FiAlertCircle className="w-3.5 h-3.5" />}
          <span className="text-xs font-medium tabular-nums">
            {charCount.toLocaleString()} / {charLimit.toLocaleString()}
          </span>
        </div>
      </div>

      {errors.content && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <FiAlertCircle className="w-3 h-3" />
          {errors.content}
        </p>
      )}

      {/* Media upload area */}
      {mediaUrl ? (
        <div className="relative group rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50">
          <img
            src={typeof mediaUrl === 'string' ? mediaUrl : URL.createObjectURL(mediaUrl)}
            alt="Post media"
            className="w-full max-h-64 object-cover"
          />
          {!disabled && (
            <button
              onClick={onMediaRemove}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : !disabled && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed
            cursor-pointer transition-all text-sm
            ${dragActive
              ? 'border-indigo-400 bg-indigo-50 text-indigo-600'
              : 'border-zinc-200 bg-zinc-50/50 text-zinc-400 hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-500'}
          `}
        >
          <FiImage className="w-5 h-5" />
          <span>{dragActive ? 'Drop image here' : 'Add image or drag & drop'}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Cross-account repost */}
      {onRepostConfigChange && !disabled && (
        <div className={`rounded-xl border p-4 space-y-3 ${repostEnabled ? 'border-indigo-200 bg-indigo-50/30' : 'border-zinc-200 bg-zinc-50/50'}`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={repostEnabled}
              onChange={(e) => onRepostConfigChange({
                enabled: e.target.checked,
                target_account: otherAccount,
                delay_minutes: repostDelay,
              })}
              className="w-4 h-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <span className="text-sm font-medium text-zinc-800">
                Auto-repost to another account
              </span>
              <p className="text-xs text-zinc-500 mt-0.5">
                Automatically share this post from a second LinkedIn account after publishing
              </p>
            </div>
          </label>

          {repostEnabled && (
            <div className="ml-7 space-y-3">
              {/* Visual flow: source -> target */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-zinc-200">
                {/* Source */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    account === 'personal' ? 'bg-violet-100 text-violet-600' : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    {account === 'personal' ? <FiUser className="w-4 h-4" /> : <FiBriefcase className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-800 capitalize">{account}</p>
                    <p className="text-[10px] text-zinc-400">Original post</p>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center gap-1 text-zinc-300">
                  <div className="w-8 h-px bg-zinc-300" />
                  <FiRepeat className="w-4 h-4 text-indigo-400" />
                  <div className="w-8 h-px bg-zinc-300" />
                </div>

                {/* Target */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    otherAccount === 'personal' ? 'bg-violet-100 text-violet-600' : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    {otherAccount === 'personal' ? <FiUser className="w-4 h-4" /> : <FiBriefcase className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-800 capitalize">{otherAccount}</p>
                    <p className="text-[10px] text-zinc-400">Native repost</p>
                  </div>
                </div>
              </div>

              {/* Delay picker */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-zinc-600">Repost timing:</span>
                <select
                  value={repostDelay}
                  onChange={(e) => onRepostConfigChange({
                    enabled: true,
                    target_account: otherAccount,
                    delay_minutes: parseInt(e.target.value),
                  })}
                  className="px-2.5 py-1.5 rounded-lg border border-zinc-200 text-xs bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  {REPOST_DELAY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="text-xs text-zinc-400">after the original publishes</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
